import { useUpdateUserSettings } from 'features/user';
import { useCallback } from 'react';
import { useUpdateTasksMutation } from 'shared/hooks/queries/use-update-tasks-mutation';
import { useUpdateRelationshipsReferences } from 'shared/hooks/use-update-relationships-references';
import { ID } from 'shared/types/id';
import { overdueSectionID } from 'shared/types/orderable-section';
import { RelationshipUpdate } from 'shared/types/relationship';
import { GroupByMode, SortingMode } from 'shared/types/sorting-mode';
import { Task } from 'shared/types/task';
import { UserSettings } from 'shared/types/user-settings';

export const useTodayOnReorder = (groupBy: GroupByMode | undefined | null) => {
  const updateSettings = useUpdateUserSettings();
  const { mutate: updateTasks } = useUpdateTasksMutation();
  const updateRelationships = useUpdateRelationshipsReferences();

  return useCallback(
    (
      updatedSections: { id: string; taskSorting: string[] }[],
      updatedTasks: Task[],
      relationshipUpdates?: RelationshipUpdate[],
    ) => {
      // Handle task updates (e.g. reparenting/move properties)
      if (updatedTasks.length) {
        updateTasks(updatedTasks);
      }

      const currentGroupMode = groupBy ?? GroupByMode.Deadline;

      const newOrder = updatedSections.reduce<Record<ID, ID[]>>(
        (acc, section) => {
          if (section.id === overdueSectionID) {
            return acc;
          }
          acc[section.id] = section.taskSorting;
          return acc;
        },
        {},
      );

      if (Object.keys(newOrder).length) {
        const legacySettings: Partial<UserSettings> = {
          todoSortingMode: SortingMode.Custom,
        };
        if (groupBy === GroupByMode.None || groupBy === GroupByMode.Deadline) {
          legacySettings.todoSorting = newOrder;
        }
        if (groupBy === GroupByMode.Goal) {
          legacySettings.todayGroupSorting = { goal: { sorting: newOrder } };
        }
        if (groupBy === GroupByMode.LifeArea) {
          legacySettings.todayGroupSorting = {
            lifeArea: { sorting: newOrder },
          };
        }
        if (groupBy === GroupByMode.Priority) {
          legacySettings.todayGroupSorting = {
            priority: { sorting: newOrder },
          };
        }
        if (groupBy === GroupByMode.Type) {
          legacySettings.todayGroupSorting = {
            type: { sorting: newOrder },
          };
        }
        if (groupBy === GroupByMode.DayPart) {
          legacySettings.todayGroupSorting = {
            dayPart: { sorting: newOrder },
          };
        }

        updateSettings({
          customTaskOrder: {
            today: {
              [currentGroupMode]: newOrder,
            },
          },
          filters: {
            today: {
              sortingMode: SortingMode.Custom,
            },
          },
          // support for old iOS ordering
          ...legacySettings,
        });
      }

      // Handle relationship updates if provided (e.g. changing tags/goals via dnd)
      if (relationshipUpdates?.length) {
        updateRelationships(relationshipUpdates);
      }
    },
    [groupBy, updateSettings, updateTasks, updateRelationships],
  );
};
